import React, { useContext, useEffect, useState } from "react";

import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useDropzone } from "react-dropzone";

import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { parse } from "csv-parse/browser/esm";

export interface FileToReturn extends File {
  csvHeaders?: boolean;
}

interface DragAndDropProps {
  acceptedFileTypes: { [key: string]: string[] };
  setDragAndDropFiles: (files: FileToReturn[]) => void;
  maxAcceptedFiles?: number;
  files: FileToReturn[];
  setFiles: React.Dispatch<React.SetStateAction<FileToReturn[]>>;
  removeFilePreview?: (index: number) => void;
}

const DragAndDrop: React.FC<DragAndDropProps> = ({
  acceptedFileTypes,
  setDragAndDropFiles,
  maxAcceptedFiles,
  files,
  setFiles,
}) => {
  const { showError } = useContext(SnackbarContext);
  const [csvPreviewData, setCsvPreviewData] = useState<
    Map<
      string,
      {
        columns: GridColDef[];
        rows: Array<{ id: string; [key: string]: string }>;
        headers: boolean;
        processed: boolean; // checks if the file had already been loaded into the preview
      }
    >
  >(new Map());

  useEffect(() => {
    if (files.length === 0) {
      setCsvPreviewData(new Map());
    }
  }, [files]);

  const updateFiles = (newState: File[]) => {
    if (maxAcceptedFiles && newState.length > maxAcceptedFiles) {
      showError(
        new Error(
          `Maximum of ${maxAcceptedFiles} file(s) allowed.`
        ) as GrpcError
      );
      return;
    }
    setFiles([...newState]);
    const updatedFiles = newState.map((file) => {
      if (file.type.startsWith("text/csv")) {
        const csvHeaders = csvPreviewData.has(file.name)
          ? csvPreviewData.get(file.name)?.headers
          : false;
        return Object.assign(file, { csvHeaders }) as FileToReturn;
      }
      return file as FileToReturn;
    });
    setDragAndDropFiles([...updatedFiles]);
  };

  const onDrop = (acceptedFiles: File[]) => {
    updateFiles([...files, ...acceptedFiles]);

    acceptedFiles.forEach(async (file) => {
      if (file.type.startsWith("text/csv")) {
        await updateCSVPreview(file);
      }
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
  });

  const displayIMGPreview = (file: File) => {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
          style={{ width: "500px" }}
        />
      </Box>
    );
  };
  const displayCSVPreview = (file: File) => {
    return (
      <Box>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            columns={csvPreviewData.get(file.name)?.columns || []}
            rows={csvPreviewData.get(file.name)?.rows || []}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 50 },
              },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={csvPreviewData.get(file.name)?.headers}
                value={csvPreviewData.get(file.name)?.headers}
                onChange={async (e) => {
                  setCsvPreviewData((currentData) => {
                    const existingData = currentData.get(file.name);
                    if (existingData) {
                      currentData.set(file.name, {
                        ...existingData,
                        headers: e.target.checked,
                        processed: true, // Mark as processed so we don't force the file headers as true again
                      });
                    }
                    return currentData;
                  });
                  await updateCSVPreview(file);
                  updateFiles(files);
                }}
              />
            }
            label="Include Headers"
          />
        </Box>
      </Box>
    );
  };
  const updateCSVPreview = async (file: File) => {
    const readFileAsText = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          reject(new Error("Error reading file"));
        };
        reader.readAsText(file);
      });
    };

    try {
      const text = await readFileAsText(file);

      parse(text, { skip_empty_lines: true }, (err, output: string[][]) => {
        if (err || output.length === 0) {
          return;
        }

        let columnDefs: GridColDef[];
        let headers: boolean;

        // checks on first load the if the first column of the first row is "user_id" and if so load in as headers: true
        if (
          output.length > 0 &&
          output[0].length > 0 &&
          output[0][0].trim().includes("_id") &&
          (!csvPreviewData.has(file.name) ||
            !csvPreviewData.get(file.name)?.processed)
        ) {
          columnDefs = output[0].map((header) => ({
            field: header,
            headerName: header,
            flex: 1,
          }));
          headers = true;
          output.shift(); // Remove headers from rows
        } else if (
          !csvPreviewData.has(file.name) ||
          !csvPreviewData.get(file.name)?.headers
        ) {
          columnDefs = Array.from({ length: output[0].length }, (_, index) => ({
            field: index.toString(),
            headerName: index.toString(),
            flex: 1,
          }));
          headers = false;
        } else {
          columnDefs = output[0].map((header) => ({
            field: header,
            headerName: header,
            flex: 1,
          }));
          headers = true;
          output.shift(); // Remove headers from rows
        }

        const rows = output.map((row: string[], rowIndex: number) => {
          const rowData: { [key: string]: string } = {};
          row.forEach((cell, colIndex) => {
            const headerName = columnDefs[colIndex]?.headerName;
            if (headerName) {
              rowData[headerName] = cell;
            }
          });
          return {
            id: rowIndex.toString(),
            ...rowData,
          };
        });

        setCsvPreviewData((prevData) => {
          const newData = new Map(prevData);
          newData.set(file.name, {
            columns: columnDefs,
            rows,
            headers,
            processed: true,
          });
          return newData;
        });

        setFiles((prevFiles) => {
          return prevFiles.map((prevFile) => {
            if (prevFile.name === file.name) {
              return Object.assign(prevFile, {
                csvHeaders: headers,
              }) as FileToReturn;
            }
            return prevFile as FileToReturn;
          });
        });
      });
    } catch (error) {
      console.error("Error processing file:", error);
    }
  };

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed #ccc",
          borderRadius: "8px",
          padding: "20px",
          textAlign: "center",
          backgroundColor: isDragActive ? "#f0f8ff" : "#fafafa",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? isDragActive
                ? "#505050"
                : "#101010"
              : isDragActive
              ? "#f0f8ff"
              : "#fafafa",
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} data-testid="drag-and-drop" />
        <Typography variant="body1">
          {isDragActive
            ? "Drop the files here ..."
            : "Drag & drop some files here, or click to select files"}
        </Typography>
      </Box>

      <Box sx={{ mt: 2 }}>
        {files.map((file, index) => (
          <Box key={index} sx={{ mt: 1 }}>
            <Typography sx={{ display: "inline" }}>{file.name}</Typography>
            <Typography
              color="red"
              sx={{ cursor: "pointer", display: "inline", padding: "0 8px" }}
              onClick={() => {
                updateFiles(files.filter((_, i) => i !== index));
              }}
            >
              x
            </Typography>
            {file.type.startsWith("image/") && displayIMGPreview(file)}
            {file.type.startsWith("text/csv") &&
              csvPreviewData.has(file.name) &&
              displayCSVPreview(file)}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default DragAndDrop;
